import React, { useState, useEffect, useCallback } from "react";
import { X, Trash2, UserPlus, PlusCircle, Shield, Edit2, Check, XCircle } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { useRemoteRegistry } from "../registry/RemoteRegistryContext";

const API = (process.env.SHELL_REGISTRY_API_URL as string) ?? "";

function deriveNameFromEmail(email: string): string {
  const local = email.split("@")[0];
  if (!local.includes(".")) return "";
  return local.split(".").map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(" ");
}

interface Role {
  id: number;
  name: string;
  description: string | null;
  is_system: boolean;
}

interface RoleUser {
  user_email: string;
  user_name: string | null;
  assigned_by: string | null;
}

interface RoleApp {
  id: number;
  app_url: string;
  app_label: string | null;
}

interface KnownUser {
  email: string;
  name: string | null;
}

interface Props {
  onClose: () => void;
}

export function AdminModal({ onClose }: Props) {
  const { user } = useAuth();
  const { allRemotes } = useRemoteRegistry();
  const email = user.username;

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roleUsers, setRoleUsers] = useState<RoleUser[]>([]);
  const [roleApps, setRoleApps] = useState<RoleApp[]>([]);

  const [showNewRoleForm, setShowNewRoleForm] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");

  const [editingRole, setEditingRole] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const [knownUsers, setKnownUsers] = useState<KnownUser[]>([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedMemberKey, setSelectedMemberKey] = useState("");
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberName, setNewMemberName] = useState("");
  const [memberNameAutoFilled, setMemberNameAutoFilled] = useState(false);

  const [showAddApp, setShowAddApp] = useState(false);
  const [newAppUrl, setNewAppUrl] = useState("");

  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    const r = await fetch(`${API}/api/shell/admin/roles?email=${encodeURIComponent(email)}`);
    const data = await r.json();
    setRoles(data.roles ?? []);
  }, [email]);

  const fetchRoleDetail = useCallback(async (role: Role) => {
    const [usersRes, appsRes] = await Promise.all([
      fetch(`${API}/api/shell/admin/roles/${role.id}/users?email=${encodeURIComponent(email)}`),
      fetch(`${API}/api/shell/admin/roles/${role.id}/apps?email=${encodeURIComponent(email)}`),
    ]);
    const [usersData, appsData] = await Promise.all([usersRes.json(), appsRes.json()]);
    setRoleUsers(usersData.users ?? []);
    setRoleApps(appsData.apps ?? []);
  }, [email]);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  useEffect(() => {
    fetch(`${API}/api/shell/users?email=${encodeURIComponent(email)}`)
      .then((r) => r.json())
      .then((data) => setKnownUsers(data.users ?? []))
      .catch(() => {});
  }, [email]);

  function selectRole(role: Role) {
    setSelectedRole(role);
    setEditingRole(false);
    setShowAddMember(false);
    setShowAddApp(false);
    setError(null);
    fetchRoleDetail(role);
  }

  async function handleCreateRole(e: React.FormEvent) {
    e.preventDefault();
    if (!newRoleName.trim()) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/shell/admin/roles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: newRoleName.trim(), description: newRoleDesc.trim() || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error);
      await fetchRoles();
      setNewRoleName("");
      setNewRoleDesc("");
      setShowNewRoleForm(false);
      selectRole(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleSaveEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRole || !editName.trim()) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/shell/admin/roles/${selectedRole.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, name: editName.trim(), description: editDesc.trim() || null }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      const updated = { ...selectedRole, name: editName.trim(), description: editDesc.trim() || null };
      setSelectedRole(updated);
      setRoles((prev) => prev.map((ro) => ro.id === updated.id ? updated : ro));
      setEditingRole(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleDeleteRole() {
    if (!selectedRole || !confirm(`Delete role "${selectedRole.name}"? This cannot be undone.`)) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/shell/admin/roles/${selectedRole.id}?email=${encodeURIComponent(email)}`,
        { method: "DELETE" }
      );
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setSelectedRole(null);
      setRoleUsers([]);
      setRoleApps([]);
      await fetchRoles();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  function handleMemberSelectChange(val: string) {
    setSelectedMemberKey(val);
    if (val === "" || val === "__manual__") {
      setNewMemberEmail("");
      setNewMemberName("");
      setMemberNameAutoFilled(false);
    } else {
      const u = knownUsers.find((ku) => ku.email === val);
      if (u) {
        setNewMemberEmail(u.email);
        setNewMemberName(u.name ?? "");
        setMemberNameAutoFilled(false);
      }
    }
  }

  function handleManualEmailChange(val: string) {
    setNewMemberEmail(val);
    if (memberNameAutoFilled || newMemberName === "") {
      const derived = deriveNameFromEmail(val);
      setNewMemberName(derived);
      setMemberNameAutoFilled(derived !== "");
    }
  }

  function handleMemberNameChange(name: string) {
    setNewMemberName(name);
    setMemberNameAutoFilled(false);
  }

  async function handleAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRole || !newMemberEmail.trim()) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/shell/admin/roles/${selectedRole.id}/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, userEmail: newMemberEmail.trim(), userName: newMemberName.trim() || null }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setNewMemberEmail("");
      setNewMemberName("");
      setMemberNameAutoFilled(false);
      setShowAddMember(false);
      await fetchRoleDetail(selectedRole);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleRemoveMember(userEmail: string) {
    if (!selectedRole) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/shell/admin/roles/${selectedRole.id}/users/${encodeURIComponent(userEmail)}?email=${encodeURIComponent(email)}`,
        { method: "DELETE" }
      );
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      await fetchRoleDetail(selectedRole);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleAddApp(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedRole || !newAppUrl) return;
    const app = allRemotes.find((a) => a.url === newAppUrl);
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(`${API}/api/shell/admin/roles/${selectedRole.id}/apps`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, appUrl: newAppUrl, appLabel: app?.label ?? null }),
      });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      setNewAppUrl("");
      setShowAddApp(false);
      await fetchRoleDetail(selectedRole);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  async function handleRemoveApp(appId: number) {
    if (!selectedRole) return;
    setWorking(true);
    setError(null);
    try {
      const r = await fetch(
        `${API}/api/shell/admin/roles/${selectedRole.id}/apps/${appId}?email=${encodeURIComponent(email)}`,
        { method: "DELETE" }
      );
      if (!r.ok) { const d = await r.json(); throw new Error(d.error); }
      await fetchRoleDetail(selectedRole);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setWorking(false);
    }
  }

  const assignedUrls = new Set(roleApps.map((a) => a.app_url));
  const availableApps = allRemotes.filter((a) => !assignedUrls.has(a.url));

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <h2 style={s.title}>Admin</h2>
          <button style={s.closeBtn} onClick={onClose}><X size={20} /></button>
        </div>

        <div style={s.body}>
          {/* Left: role list */}
          <div style={s.roleList}>
            <div style={s.roleListLabel}>Roles</div>
            {roles.map((role) => (
              <button
                key={role.id}
                style={{ ...s.roleItem, ...(selectedRole?.id === role.id ? s.roleItemActive : {}) }}
                onClick={() => selectRole(role)}
              >
                {role.is_system && <Shield size={12} style={{ flexShrink: 0, opacity: 0.6 }} />}
                <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {role.name}
                </span>
              </button>
            ))}

            {!showNewRoleForm ? (
              <button style={s.newRoleBtn} onClick={() => setShowNewRoleForm(true)}>
                <PlusCircle size={13} style={{ marginRight: 5 }} /> New role
              </button>
            ) : (
              <form onSubmit={handleCreateRole} style={s.newRoleForm}>
                <input
                  style={s.compactInput}
                  placeholder="Role name"
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  autoFocus
                />
                <input
                  style={{ ...s.compactInput, marginTop: 5 }}
                  placeholder="Description (optional)"
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                />
                <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
                  <button type="submit" style={s.saveBtn} disabled={working}>Add</button>
                  <button type="button" style={s.cancelSmBtn}
                    onClick={() => { setShowNewRoleForm(false); setNewRoleName(""); setNewRoleDesc(""); }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Right: role detail */}
          <div style={s.detail}>
            {error && <div style={s.errorBanner}>{error}</div>}

            {!selectedRole ? (
              <div style={s.emptyDetail}>Select a role to manage it</div>
            ) : (
              <>
                {/* Role header */}
                <div style={s.roleHeader}>
                  {editingRole ? (
                    <form onSubmit={handleSaveEdit} style={{ flex: 1 }}>
                      <input style={s.editNameInput} value={editName}
                        onChange={(e) => setEditName(e.target.value)} autoFocus />
                      <input style={{ ...s.compactInput, marginTop: 6 }} value={editDesc}
                        placeholder="Description (optional)"
                        onChange={(e) => setEditDesc(e.target.value)} />
                      <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                        <button type="submit" style={s.saveBtn} disabled={working}>
                          <Check size={13} style={{ marginRight: 4 }} /> Save
                        </button>
                        <button type="button" style={s.cancelSmBtn} onClick={() => setEditingRole(false)}>
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={s.roleName}>{selectedRole.name}</span>
                          {selectedRole.is_system && (
                            <span style={s.systemBadge}>system</span>
                          )}
                        </div>
                        {selectedRole.description && (
                          <div style={s.roleDesc}>{selectedRole.description}</div>
                        )}
                      </div>
                      {!selectedRole.is_system && (
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={s.iconBtn} title="Edit role"
                            onClick={() => { setEditingRole(true); setEditName(selectedRole.name); setEditDesc(selectedRole.description ?? ""); }}>
                            <Edit2 size={14} />
                          </button>
                          <button style={{ ...s.iconBtn, color: "#d93025" }} title="Delete role"
                            onClick={handleDeleteRole} disabled={working}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div style={s.divider} />

                {/* Members */}
                <div style={s.sectionLabel}>Members</div>
                {roleUsers.length === 0 && <p style={s.empty}>No members assigned.</p>}
                {roleUsers.map((u) => (
                  <div key={u.user_email} style={s.listRow}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{u.user_name || u.user_email}</div>
                      {u.user_name && <div style={{ fontSize: 12, color: "#888" }}>{u.user_email}</div>}
                    </div>
                    <button style={s.removeBtn} onClick={() => handleRemoveMember(u.user_email)} disabled={working}>
                      <XCircle size={15} />
                    </button>
                  </div>
                ))}
                {!showAddMember ? (
                  <button style={s.addBtn} onClick={() => setShowAddMember(true)}>
                    <UserPlus size={13} style={{ marginRight: 5 }} /> Add member
                  </button>
                ) : (
                  <form onSubmit={handleAddMember} style={s.inlineForm}>
                    {(() => {
                      const assigned = new Set(roleUsers.map((u) => u.user_email));
                      const available = knownUsers.filter((u) => !assigned.has(u.email));
                      return (
                        <select style={s.compactInput} value={selectedMemberKey}
                          onChange={(e) => handleMemberSelectChange(e.target.value)} autoFocus>
                          <option value="">Select a user…</option>
                          {available.map((u) => (
                            <option key={u.email} value={u.email}>
                              {u.name ? `${u.name} (${u.email})` : u.email}
                            </option>
                          ))}
                          <option value="__manual__">Enter email manually…</option>
                        </select>
                      );
                    })()}
                    {selectedMemberKey === "__manual__" && (
                      <>
                        <input style={{ ...s.compactInput, marginTop: 5 }} type="email"
                          placeholder="user@skillable.com" value={newMemberEmail}
                          onChange={(e) => handleManualEmailChange(e.target.value)}
                          required autoFocus />
                        <input style={{ ...s.compactInput, marginTop: 5 }} placeholder="Name (optional)"
                          value={newMemberName} onChange={(e) => handleMemberNameChange(e.target.value)} />
                      </>
                    )}
                    <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
                      <button type="submit" style={s.saveBtn}
                        disabled={working || !newMemberEmail.trim()}>Add</button>
                      <button type="button" style={s.cancelSmBtn}
                        onClick={() => { setShowAddMember(false); setSelectedMemberKey(""); setNewMemberEmail(""); setNewMemberName(""); setMemberNameAutoFilled(false); }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                <div style={s.divider} />

                {/* Apps */}
                <div style={s.sectionLabel}>
                  Allowed Apps
                  <span style={s.sectionHint}>
                    {roleApps.length === 0 ? " — no apps granted (members see nothing)" : ""}
                  </span>
                </div>
                {roleApps.map((a) => (
                  <div key={a.id} style={s.listRow}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{a.app_label || a.app_url}</div>
                      {a.app_label && <div style={{ fontSize: 12, color: "#888" }}>{a.app_url}</div>}
                    </div>
                    <button style={s.removeBtn} onClick={() => handleRemoveApp(a.id)} disabled={working}>
                      <XCircle size={15} />
                    </button>
                  </div>
                ))}
                {!showAddApp ? (
                  <button style={s.addBtn} onClick={() => setShowAddApp(true)}>
                    <PlusCircle size={13} style={{ marginRight: 5 }} /> Add app
                  </button>
                ) : (
                  <form onSubmit={handleAddApp} style={s.inlineForm}>
                    <select style={s.compactInput} value={newAppUrl}
                      onChange={(e) => setNewAppUrl(e.target.value)} required autoFocus>
                      <option value="">Select an app…</option>
                      {availableApps.map((a) => (
                        <option key={a.url} value={a.url}>{a.label}</option>
                      ))}
                    </select>
                    <div style={{ display: "flex", gap: 5, marginTop: 6 }}>
                      <button type="submit" style={s.saveBtn} disabled={working || !newAppUrl}>Add</button>
                      <button type="button" style={s.cancelSmBtn}
                        onClick={() => { setShowAddApp(false); setNewAppUrl(""); }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 8, width: 720, maxWidth: "95vw", height: 560, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 4px 24px rgba(0,0,0,0.18)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid #e0e0e0", flexShrink: 0 },
  title: { fontSize: 17, fontWeight: 600, margin: 0 },
  closeBtn: { background: "none", border: "none", cursor: "pointer", color: "#5f6368", display: "flex", padding: 4 },
  body: { display: "flex", flex: 1, overflow: "hidden" },
  roleList: { width: 190, flexShrink: 0, borderRight: "1px solid #e0e0e0", overflowY: "auto", padding: "10px 8px", display: "flex", flexDirection: "column", gap: 2 },
  roleListLabel: { fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: "0.05em", padding: "4px 6px 8px" },
  roleItem: { display: "flex", alignItems: "center", gap: 6, padding: "7px 8px", borderRadius: 5, background: "none", border: "none", cursor: "pointer", fontSize: 13.5, color: "#333", width: "100%" },
  roleItemActive: { background: "#e6f3ed", color: "#136945", fontWeight: 600 },
  newRoleBtn: { display: "flex", alignItems: "center", background: "none", border: "1px dashed #ccc", borderRadius: 5, padding: "6px 8px", fontSize: 12.5, color: "#777", cursor: "pointer", marginTop: 6, width: "100%" },
  newRoleForm: { padding: "8px 4px" },
  detail: { flex: 1, overflowY: "auto", padding: "16px 20px" },
  emptyDetail: { fontSize: 14, color: "#aaa", fontStyle: "italic", marginTop: 20, textAlign: "center" as const },
  errorBanner: { background: "#fce8e6", color: "#c62828", border: "1px solid #f5c6cb", borderRadius: 6, padding: "8px 12px", fontSize: 13, marginBottom: 12 },
  roleHeader: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 4 },
  roleName: { fontSize: 16, fontWeight: 700 },
  roleDesc: { fontSize: 13, color: "#666", marginTop: 3 },
  systemBadge: { fontSize: 10, background: "#f0f0f0", color: "#666", borderRadius: 4, padding: "2px 6px", fontWeight: 600 },
  sectionLabel: { fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase" as const, letterSpacing: "0.05em", marginBottom: 8 },
  sectionHint: { fontSize: 11, fontWeight: 400, color: "#aaa", textTransform: "none" as const, letterSpacing: 0 },
  divider: { height: 1, background: "#f0f0f0", margin: "14px 0" },
  listRow: { display: "flex", alignItems: "center", padding: "7px 0", borderBottom: "1px solid #f5f5f5" },
  removeBtn: { background: "none", border: "none", cursor: "pointer", color: "#ccc", padding: 4, display: "flex", flexShrink: 0 },
  addBtn: { display: "flex", alignItems: "center", background: "none", border: "1px dashed #ccc", borderRadius: 5, padding: "6px 10px", fontSize: 13, color: "#777", cursor: "pointer", marginTop: 8, width: "100%" },
  inlineForm: { marginTop: 8, padding: "10px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #eee" },
  compactInput: { width: "100%", padding: "6px 8px", border: "1px solid #ddd", borderRadius: 5, fontSize: 13, boxSizing: "border-box" as const },
  editNameInput: { width: "100%", padding: "6px 8px", border: "1px solid #136945", borderRadius: 5, fontSize: 15, fontWeight: 700, boxSizing: "border-box" as const },
  saveBtn: { background: "#136945", color: "#fff", border: "none", borderRadius: 5, padding: "5px 12px", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center" },
  cancelSmBtn: { background: "none", border: "1px solid #ddd", borderRadius: 5, padding: "5px 10px", cursor: "pointer", fontSize: 13 },
  iconBtn: { background: "none", border: "none", cursor: "pointer", color: "#888", padding: 4, display: "flex", alignItems: "center", borderRadius: 4 },
  empty: { fontSize: 13, color: "#aaa", fontStyle: "italic", margin: "4px 0 8px" },
};
